/**
 * KCIS™ Pro — AI Summary panel (Sprint 6B4).
 *
 * Renders the 5 blocks of the active Claude Sonnet 4.5-generated
 * wellness synthesis. Each block can be individually overridden
 * (practitioner_pro / super_admin only). Block override is stored on the
 * same record and the LLM-original is preserved. Regenerate spawns a new
 * version_tag.
 *
 * Compliance: never display block content if compliance_filter_applied
 * is missing or false — render an error card instead. The backend strips
 * forbidden vocab before persistence so this is an extra safety net.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Sparkles,
  RefreshCw,
  Pencil,
  Check,
  X,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { Button } from "@/components/ui/button";

const BLOCKS = [
  { key: "wellness_synthesis", label: "Wellness synthesis" },
  { key: "emotional_synthesis", label: "Emotional synthesis" },
  { key: "pathway_reasoning", label: "Pathway reasoning" },
  { key: "practitioner_review_notes", label: "Practitioner review notes" },
  { key: "escalation_explanation", label: "Escalation explanation" },
];

function BlockCard({ block, value, originalValue, overridden, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value || "");
  }, [value, editing]);

  const submit = async () => {
    if (!draft.trim()) {
      toast.error("Block text cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      await onSave(block.key, draft.trim(), reason.trim() || null);
      setEditing(false);
      setReason("");
    } finally {
      setSaving(false);
    }
  };

  // Hide entirely if no LLM block AND no override (e.g., escalation block when no escalation).
  if (!value && !originalValue) return null;

  return (
    <div
      data-testid={`ai-summary-block-${block.key}`}
      className={`rounded-xl border p-4 ${
        overridden ? "border-purple-300 bg-purple-50/40" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="kcis-overline text-purple-900">{block.label}</span>
          {overridden ? (
            <span
              data-testid={`ai-summary-block-${block.key}-overridden`}
              className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-900 text-white"
            >
              Practitioner override
            </span>
          ) : null}
        </div>
        {canEdit && !editing ? (
          <Button
            size="sm"
            variant="ghost"
            data-testid={`ai-summary-block-${block.key}-edit`}
            onClick={() => setEditing(true)}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
        ) : null}
      </div>

      {!editing ? (
        <p
          data-testid={`ai-summary-block-${block.key}-text`}
          className="mt-2 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed"
        >
          {value}
        </p>
      ) : (
        <div className="mt-2 space-y-2">
          <textarea
            data-testid={`ai-summary-block-${block.key}-input`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-purple-400 focus:outline-none min-h-[96px]"
          />
          <textarea
            data-testid={`ai-summary-block-${block.key}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for the edit (recommended — logged immutably)"
            className="w-full text-xs rounded-lg border border-slate-200 px-3 py-2 focus:border-purple-400 focus:outline-none min-h-[56px]"
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid={`ai-summary-block-${block.key}-cancel`}
              onClick={() => {
                setEditing(false);
                setDraft(value || "");
                setReason("");
              }}
              disabled={saving}
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              data-testid={`ai-summary-block-${block.key}-save`}
              onClick={submit}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              ) : (
                <Check className="w-3.5 h-3.5 mr-1" />
              )}
              Save
            </Button>
          </div>
          {overridden && originalValue ? (
            <div className="mt-2 text-[11px] text-slate-500">
              <div className="kcis-overline text-slate-500">LLM original</div>
              <div className="mt-1 whitespace-pre-wrap">{originalValue}</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function AiSummaryPanel({
  assessmentId,
  summary,
  loading,
  canRegenerate,
  canEditBlocks,
  onChanged,
}) {
  const [regenerating, setRegenerating] = useState(false);

  const overrides = summary?.practitioner_override || {};
  const blocks = summary?.blocks || {};

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await api.post(`/nutrient/${assessmentId}/ai-summary/regenerate`);
      if (r?.data?.ok) {
        toast.success("Regeneration queued — refreshing shortly.");
        setTimeout(() => onChanged?.(), 1800);
      } else {
        toast.error("Could not queue regeneration");
      }
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.message("AI summaries are temporarily disabled.");
      } else if (err?.response?.status === 403) {
        toast.error("You do not have permission to regenerate.");
      } else if (err?.response?.status === 404) {
        toast.error("Nutrient results required first.");
      } else {
        toast.error("Regeneration failed");
      }
    } finally {
      setRegenerating(false);
    }
  };

  const saveBlock = useCallback(
    async (blockName, newText, reason) => {
      try {
        const r = await api.patch(
          `/nutrient/${assessmentId}/ai-summary/block/${blockName}`,
          { new_text: newText, reason }
        );
        if (r?.data?.ok) {
          toast.success("Block override saved");
          onChanged?.();
        } else {
          toast.error(r?.data?.error || "Could not save");
        }
      } catch (err) {
        if (err?.response?.status === 503) {
          toast.message("Block override is temporarily disabled.");
        } else if (err?.response?.status === 403) {
          toast.error("You do not have permission to edit blocks.");
        } else {
          toast.error(formatApiError(err, "Could not save"));
        }
        throw err;
      }
    },
    [assessmentId, onChanged]
  );

  const headerMeta = useMemo(() => {
    if (!summary) return null;
    return (
      <span className="text-xs text-slate-500">
        {summary.version_tag || "v1"} ·{" "}
        {summary.model_provider ? `${summary.model_provider}/` : ""}
        {summary.model_name || "model"} ·{" "}
        {summary.generated_at ? new Date(summary.generated_at).toLocaleString() : ""}
      </span>
    );
  }, [summary]);

  return (
    <div
      data-testid="practitioner-ai-summary-panel"
      className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-900" />
          <div>
            <div className="font-medium text-slate-900">AI wellness synthesis</div>
            {headerMeta}
          </div>
        </div>
        {canRegenerate ? (
          <Button
            variant="outline"
            size="sm"
            data-testid="ai-summary-regenerate"
            onClick={regenerate}
            disabled={regenerating}
          >
            {regenerating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
            )}
            Regenerate
          </Button>
        ) : null}
      </div>

      {loading ? (
        <div className="px-4 py-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading AI summary…
        </div>
      ) : !summary ? (
        <div
          data-testid="ai-summary-empty"
          className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-600 bg-slate-50/60"
        >
          No AI summary yet for this assessment.
          {canRegenerate ? (
            <span className="block mt-1 text-slate-500">
              Use “Regenerate” above to generate the first version.
            </span>
          ) : null}
        </div>
      ) : !summary.compliance_filter_applied ? (
        <div
          data-testid="ai-summary-compliance-error"
          className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
        >
          This summary did not pass compliance filtering and is hidden. Regenerate to retry.
        </div>
      ) : (
        <div className="space-y-3">
          {BLOCKS.map((b) => {
            const overridden = b.key in overrides;
            const effective = overridden ? overrides[b.key] : blocks[b.key];
            const original = blocks[b.key];
            return (
              <BlockCard
                key={b.key}
                block={b}
                value={effective}
                originalValue={original}
                overridden={overridden}
                canEdit={canEditBlocks}
                onSave={saveBlock}
              />
            );
          })}
          {summary.compliance_filter_redactions > 0 ? (
            <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              Compliance filter applied · {summary.compliance_filter_redactions} redaction
              {summary.compliance_filter_redactions === 1 ? "" : "s"}.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
